import React, { useEffect } from "react";
import { righeAddizionali } from "./RisultatoRegole";

// Busta paga simulata impaginata come il Libro Unico del Lavoro (LUL) del gestionale paghe
// (task 533): stesse sezioni, stesse colonne e stessi codici voce delle buste reali.
// Volutamente NON ci sono gli elementi che rendono "ufficiale" un foglio del LUL (autorizzazione
// INAIL, numero di foglio, quietanza con firma, dati della ditta): al loro posto l'avviso di
// simulazione e una filigrana, cosi' la busta non si puo' scambiare per un documento vero.
// Nessun calcolo nuovo: legge solo dettaglio_calcolo, quindi i numeri coincidono con il risultato.
// La stampa usa la classe "stampa-busta-paga" sul body (vedi @media print nel CSS della pagina).

const eur = (value) => (value == null || value === "" ? "" : Number(value).toLocaleString("it-IT", {
    minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always"
}));
const dec5 = (value) => Number(value).toLocaleString("it-IT", { minimumFractionDigits: 5, maximumFractionDigits: 5 });
const somma = (lista, campo) => lista.reduce((t, x) => t + (x[campo] || 0), 0);

// Festivita' nazionali 2026 (mese-giorno) per il calendario presenze; Pasqua 2026 = 5 aprile
const FESTIVI_2026 = new Set(["1-1", "1-6", "4-5", "4-6", "4-25", "5-1", "6-2", "8-15", "11-1", "12-8", "12-25", "12-26"]);

// Codice e descrizione delle addizionali come nel LUL (ricavati dalle etichette di righeAddizionali)
const voceAddizionale = (label) => (
    label.startsWith("Rata addizionale regionale") ? { cod: "1800", desc: "RATA ADDIZ.REGIONALE A.P." }
        : label.startsWith("Rata addizionale comunale") ? { cod: "1802", desc: "RATA ADDIZ.COMUNALE A.P." }
            : label.startsWith("Acconto") ? { cod: "9173", desc: "ACCONTO ADD.COMUNALE" }
                : label.startsWith("Addizionale regionale") ? { cod: "", desc: "ADDIZ.REGIONALE (MEDIA MENS.)" }
                    : { cod: "", desc: "ADDIZ.COMUNALE (MEDIA MENS.)" }
);

// Cella "etichetta piccola + valore", come i riquadri del LUL
const Campo = ({ label, children, className = "" }) => (
    <div className={`lul-campo ${className}`}>
        <span className="lul-label">{label}</span>
        <span className="lul-valore">{children}</span>
    </div>
);

const BustaPagaSimulata = ({ risultato, onClose }) => {
    const d = risultato.dettaglio_calcolo;
    const { retribuzione: r, mensile: m, parametri: p, monteOre } = d;
    const b = d.busta || { aggiuntive: [] };
    const aggiuntive = b.aggiuntive || [];
    const pt = r.percPartTime;
    const oreMese = p.oreMensiliContrattuali * pt;
    const retrOraria = r.retribuzioneFullTime / p.oreMensiliContrattuali;

    // --- voci della busta (codici come nel LUL) ---
    const voci = [
        { cod: "8001", desc: "LAVORO ORDINARIO ORE", ore: eur(oreMese), base: dec5(retrOraria), comp: m.lordo },
        ...aggiuntive.map(a => ({
            cod: "", desc: a.nome === "13ª" ? "TREDICESIMA MENSILITA'" : `QUATTORDICESIMA MENSILITA' (${a.quota === 0.5 ? "50%" : a.quota})`,
            comp: a.lordo
        })),
        { cod: "9191", desc: "QUOTA TFR MESE INPS", stat: b.tfrMese }
    ];
    const add = righeAddizionali(d);
    if (add) {
        add.forEach(x => voci.push({ ...voceAddizionale(x.label), tratt: x.valore }));
    } else if (m.addizionali > 0) {
        voci.push({ cod: "", desc: "ADDIZIONALI REG./COM. (IMPORTO FISSO)", tratt: m.addizionali });
    }
    const imponibile5 = m.imponibileSostitutiva + somma(aggiuntive, "imponibileSostitutiva");
    const imposta5 = (m.impostaSostitutiva || 0) + somma(aggiuntive, "impostaSostitutiva");
    if (imposta5 > 0) {
        voci.push({ cod: "9835", desc: "INCREM. ASSOG.5% L.199/25", stat: imponibile5 });
        voci.push({ cod: "9837", desc: "IMPONIBILE 5% L.199/25", stat: imponibile5 });
        voci.push({ cod: "9838", desc: "IMPOSTA SOST. 5%", tratt: imposta5 });
    }
    const sommaEsente = m.sommaEsente + somma(aggiuntive, "sommaEsente");
    if (sommaEsente > 0) voci.push({ cod: "9824", desc: "SOMMA ART.1 C.4 L.207/24", comp: sommaEsente });
    if (m.trattamentoIntegrativo > 0) voci.push({ cod: "8992", desc: "TRATTAMENTO INT. DL 3/20", comp: m.trattamentoIntegrativo });
    if (m.conguaglioUlterioreDetrazione > 0) {
        voci.push({ cod: "", desc: "ULT. DETRAZ. L.207/24 (CONGUAGLIO)", comp: m.conguaglioUlterioreDetrazione });
    }

    // --- totali (gli stessi riquadri del LUL) ---
    const totaleLordo = m.lordo + somma(aggiuntive, "lordo");
    const imponibileContr = (m.imponibileContributivo ?? m.lordo) + somma(aggiuntive, "imponibileContr");
    const contributi = m.ivs + somma(aggiuntive, "ivs");
    const impIrpef = m.imponibile + somma(aggiuntive, "imponibile");
    const irpefLorda = m.irpefLorda + somma(aggiuntive, "irpef");
    const detrazioniMese = m.detrazioneLavoro + m.ulterioreDetrazione + m.detrazioniFamiliari;
    const totDetrazioni = Math.min(detrazioniMese, m.irpefLorda);   // come il LUL: mai oltre l'IRPEF del mese
    const totIrpef = m.irpefNetta + somma(aggiuntive, "irpef");
    const totTrattenute = contributi + totIrpef + imposta5 + m.addizionali;
    const netto = b.nettoTotale ?? m.netto;

    // --- calendario presenze del mese ---
    const calendario = b.mese ? Array.from({ length: b.giorni }, (_, i) => {
        const giorno = i + 1;
        const dow = new Date(2026, b.mese - 1, giorno).getDay();
        const riposo = dow === 0 || (p.giorniSettimanali <= 5 && dow === 6);
        const festivo = FESTIVI_2026.has(`${b.mese}-${giorno}`);
        return { giorno, sigla: riposo ? "R" : festivo ? "F" : "", ore: riposo || festivo ? "" : eur(monteOre.oreGiornaliere) };
    }) : [];

    const nominativo = [risultato.nome_dipendente, risultato.cognome_dipendente].filter(Boolean).join(" ").toUpperCase();
    const meseRetribuito = b.nomeMese ? `${b.nomeMese.toUpperCase()} 2026` : "MESE TIPO 2026";
    const scatti = risultato.input_calcolo?.scatti ?? 0;
    const pr = b.progressivi;

    const stampa = () => {
        document.body.classList.add("stampa-busta-paga");
        window.print();
    };

    // Toglie la classe di stampa a fine stampa (e se si chiude la busta); ESC chiude.
    useEffect(() => {
        const fineStampa = () => document.body.classList.remove("stampa-busta-paga");
        const esc = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("afterprint", fineStampa);
        window.addEventListener("keydown", esc);
        return () => {
            fineStampa();
            window.removeEventListener("afterprint", fineStampa);
            window.removeEventListener("keydown", esc);
        };
    }, [onClose]);

    return (
        <div className="busta-paga-overlay" onClick={onClose}>
            <div className="busta-paga lul" onClick={(e) => e.stopPropagation()}>
                <div className="busta-paga-azioni">
                    <button type="button" className="btn-mini btn-mini-primary" onClick={stampa}>
                        Stampa / PDF
                    </button>
                    <button type="button" className="btn-mini" onClick={onClose}>
                        Chiudi
                    </button>
                </div>

                <div className="lul-filigrana" aria-hidden="true"><span>SIMULAZIONE</span></div>

                {/* Testata: al posto della ditta e dell'autorizzazione INAIL, l'avviso di simulazione */}
                <header className="lul-testata">
                    <div>
                        <div className="lul-titolo">SIMULAZIONE BUSTA PAGA</div>
                        <div className="lul-sottotitolo">{risultato.ccnl} · regole fiscali {d.regole}</div>
                    </div>
                    <div className="lul-avviso">
                        Simulazione a scopo indicativo.<br />
                        Non è un documento ufficiale né un foglio del Libro Unico del Lavoro.
                    </div>
                </header>

                {/* Righe anagrafiche */}
                <div className="lul-riga lul-col-4">
                    <Campo label="MESE RETRIBUITO">{meseRetribuito}</Campo>
                    <Campo label="COGNOME E NOME" className="lul-largo">{nominativo}</Campo>
                    <Campo label="DATA ASSUNZ.">—</Campo>
                    <Campo label="SCATTI ANZ.">{scatti}</Campo>
                </div>
                <div className="lul-riga lul-col-4">
                    <Campo label="CODICE FISCALE">—</Campo>
                    <Campo label="REGIONE DI RESIDENZA" className="lul-largo">{(d.addizionali?.regione || "—").toUpperCase()}</Campo>
                    <Campo label="GG. CONTR.">26</Campo>
                    <Campo label="ORE CONTR.">{eur(p.oreMensiliContrattuali)}</Campo>
                </div>
                <div className="lul-riga lul-col-4">
                    <Campo label="QUALIFICA">{(risultato.tipologia_contrattuale || "Standard").toUpperCase()}</Campo>
                    <Campo label="CCNL" className="lul-largo">{risultato.ccnl.toUpperCase()}</Campo>
                    <Campo label="% P. TIME">{pt < 1 ? eur(pt * 100) : ""}</Campo>
                    <Campo label="LIVELLO">{risultato.livello || "—"}</Campo>
                </div>

                {/* Elementi della retribuzione (tempo pieno) */}
                <div className="lul-riga lul-col-6 lul-elementi">
                    <Campo label="PAGA BASE">{eur(r.lordoTabella)}</Campo>
                    <Campo label="SCATTI ANZ.">{r.anzianita > 0 ? eur(r.anzianita) : ""}</Campo>
                    <Campo label="SUPERMIN.">{r.superminimo > 0 ? eur(r.superminimo) : ""}</Campo>
                    <Campo label="ALTRE IND.">{r.altreIndennita > 0 ? eur(r.altreIndennita) : ""}</Campo>
                    <Campo label="RETR. ORARIA CONTR.">{dec5(retrOraria)}</Campo>
                    <Campo label="RETRIB. DI FATTO">{eur(r.retribuzioneFullTime)}</Campo>
                </div>

                {/* Voci + calendario presenze */}
                <div className="lul-corpo">
                    <table className="lul-voci">
                        <thead>
                            <tr>
                                <th>CODICE</th>
                                <th>DESCRIZIONE</th>
                                <th className="num">ORE/GIORNI</th>
                                <th className="num">BASE</th>
                                <th className="num lul-competenze">COMPETENZE</th>
                                <th className="num">TRATTENUTE</th>
                                <th className="num">STATISTICHE</th>
                            </tr>
                        </thead>
                        <tbody>
                            {voci.map((v, i) => (
                                <tr key={`${v.cod}-${i}`}>
                                    <td>{v.cod}</td>
                                    <td>{v.desc}</td>
                                    <td className="num">{v.ore || ""}</td>
                                    <td className="num">{v.base || ""}</td>
                                    <td className="num lul-competenze">{v.comp != null ? eur(v.comp) : ""}</td>
                                    <td className="num">{v.tratt != null ? eur(v.tratt) : ""}</td>
                                    <td className="num">{v.stat != null ? eur(v.stat) : ""}</td>
                                </tr>
                            ))}
                            <tr className="lul-ccnl">
                                <td></td>
                                <td colSpan={6}>Codice CCNL: {risultato.ccnl}</td>
                            </tr>
                        </tbody>
                    </table>

                    <table className="lul-calendario">
                        <thead>
                            <tr><th></th><th>ore lav.</th><th>sigla</th></tr>
                        </thead>
                        <tbody>
                            {calendario.length ? calendario.map(c => (
                                <tr key={c.giorno}><td>{c.giorno}</td><td>{c.ore}</td><td>{c.sigla}</td></tr>
                            )) : (
                                <tr><td colSpan={3} className="lul-calendario-vuoto">mese tipo: nessun calendario</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Riquadri dei totali */}
                <div className="lul-riga lul-col-4 lul-totali">
                    <Campo label="TOTALE LORDO">{eur(totaleLordo)}</Campo>
                    <Campo label="IMPON. CONTR. SOC.">{eur(imponibileContr)}</Campo>
                    <Campo label="CONTRIBUTO 1 (IVS)">{eur(contributi)}</Campo>
                    <Campo label="TOT. CONTR. SOC." className="lul-evidenza">{eur(contributi)}</Campo>
                </div>
                <div className="lul-riga lul-col-4 lul-totali">
                    <Campo label="IMP. IRPEF">{eur(impIrpef)}</Campo>
                    <Campo label="IRPEF LORDA">{eur(irpefLorda)}</Campo>
                    <Campo label="TOT. DETR.">{eur(totDetrazioni)}</Campo>
                    <Campo label="TOT. TRAT. IRPEF" className="lul-evidenza">{eur(totIrpef)}</Campo>
                </div>
                <div className="lul-riga lul-col-4 lul-totali">
                    <Campo label="ADDIZ. REG./COM.">{eur(m.addizionali)}</Campo>
                    <Campo label="IMPOSTA SOST. 5%">{imposta5 > 0 ? eur(imposta5) : ""}</Campo>
                    <Campo label="TOT. TRATTENUTE" className="lul-evidenza">{eur(totTrattenute)}</Campo>
                    <Campo label="NETTO BUSTA" className="lul-netto">{eur(netto)}</Campo>
                </div>

                {/* Dati statistici, detrazioni spettanti, progressivi */}
                <div className="lul-riga lul-col-4 lul-piede">
                    <Campo label="ORE INPS">{eur(oreMese)}</Campo>
                    <Campo label="GG. INPS">{b.mese ? 26 : ""}</Campo>
                    <Campo label="IMPONIBILE INAIL">{eur(imponibileContr)}</Campo>
                    <Campo label="TFR MESE">{eur(b.tfrMese)}</Campo>
                </div>
                <div className="lul-riga lul-col-4 lul-piede">
                    <Campo label="DETRAZ. LAVORO DIP.">{eur(m.detrazioneLavoro)}</Campo>
                    <Campo label="DETRAZ. FAMILIARI">{m.detrazioniFamiliari > 0 ? eur(m.detrazioniFamiliari) : ""}</Campo>
                    <Campo label="ULTERIORE DETRAZ.">{eur(m.ulterioreDetrazione + (m.conguaglioUlterioreDetrazione || 0))}</Campo>
                    <Campo label="TRATT. INTEGRATIVO">{m.trattamentoIntegrativo > 0 ? eur(m.trattamentoIntegrativo) : ""}</Campo>
                </div>
                {pr && (
                    <div className="lul-riga lul-col-6 lul-piede">
                        <Campo label="PROGR. IMP. CONTRIB.">{eur(pr.imponibileContributivo)}</Campo>
                        <Campo label="PROGR. CONTRIB. SOC.">{eur(pr.contributi)}</Campo>
                        <Campo label="PROGR. IMPONIBILE IRPEF">{eur(pr.imponibileIrpef)}</Campo>
                        <Campo label="PROGR. IRPEF LORDA">{eur(pr.irpefLorda)}</Campo>
                        <Campo label="PROGR. TOT. DETR.">{eur(pr.detrazioni)}</Campo>
                        <Campo label="PROGR. IRPEF PAGATA">{eur(pr.irpefTrattenuta)}</Campo>
                    </div>
                )}

                <p className="lul-note">
                    SIMULAZIONE · Ipotesi: stesso stipendio ogni mese dell'anno, addizionali dell'anno precedente
                    calcolate sul reddito simulato, detrazioni sui giorni del mese, ulteriore detrazione del cuneo a
                    conguaglio di dicembre. Non comprende voci variabili (straordinari, festivi, malattia, 730) né
                    situazioni personali. Dettaglio delle ipotesi nel documento "Simulatore costi dipendenti – Analisi e scelte".
                </p>
            </div>
        </div>
    );
};

export default BustaPagaSimulata;
